// src/pages/forge/SquadProgressTracker.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  TrendingUp,
  Users,
  Target,
  CheckCircle2,
  Clock,
  Calendar,
  Award,
  BarChart3,
  Loader2,
  ChevronRight,
  Flag,
  ListChecks,
  FileText,
  ArrowLeft,
  MessageSquare,
  FileCheck,
  CalendarDays,
  Hourglass,
  Rocket
} from 'lucide-react';
import { useSquadAuth } from '@/contexts/SquadAuthContext';
import { supabase } from '@/lib/supabaseClient';

// Helper functions
const getInitials = (name: string) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleBadgeColor = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'market_analyst': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'product_lead': return 'bg-green-100 text-green-800 border-green-200';
    case 'finance_lead': return 'bg-orange-100 text-orange-800 border-orange-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getDisplayRole = (role: string) => {
  switch(role?.toLowerCase()) {
    case 'lead': return 'Project Lead';
    case 'market_analyst': return 'Market Analyst';
    case 'product_lead': return 'Product Lead';
    case 'finance_lead': return 'Finance Lead';
    default: return role || 'Team Member';
  }
};

interface MemberProgress {
  id: string;
  name: string;
  role: string;
  email: string;
  completedKPIs: number;
  totalKPIs: number;
  percentage: number;
  assessmentCompleted: boolean;
}

interface AssignedProblem {
  id: string;
  title: string;
  status: string;
  assignedAt: string;
  selectedAt?: string;
  targetDate?: string;
  milestones?: Array<{ description: string; targetDate: string; completed: boolean; completedDate?: string }>;
  solutionExpectations?: {
    deliverables: string[];
    successCriteria: string[];
    expectedOutcome: string;
  };
}

interface UpcomingTask {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  priority: string;
  status: string;
  assignedTo: string;
  assignedToName: string;
}

interface CustomerMeeting {
  id: string;
  title: string;
  meetingDate: string;
  notes?: string;
}

interface LOI {
  id: string;
  companyName: string;
  status: string;
  signedDate?: string;
}

export default function SquadProgressTracker() {
  const navigate = useNavigate();
  const { squadUser } = useSquadAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [members, setMembers] = useState<MemberProgress[]>([]);
  const [assignedProblems, setAssignedProblems] = useState<AssignedProblem[]>([]);
  const [upcomingTasks, setUpcomingTasks] = useState<UpcomingTask[]>([]);
  const [customerMeetings, setCustomerMeetings] = useState<CustomerMeeting[]>([]);
  const [lois, setLois] = useState<LOI[]>([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [completedKPIsTotal, setCompletedKPIsTotal] = useState(0);
  const [totalKPIsTotal, setTotalKPIsTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (squadUser?.squadId) {
      loadProgressData();
    }
  }, [squadUser]);

  const loadProgressData = async () => {
    setIsLoading(true);
    try {
      // 1. Load squad members with their KPI progress
      const { data: membersData, error: membersError } = await supabase
        .from('squad_members')
        .select('*')
        .eq('squad_id', squadUser?.squadId);

      if (membersError) throw membersError;

      const memberProgressList: MemberProgress[] = [];
      let totalCompleted = 0;
      let totalKPIs = 0;

      for (const member of membersData || []) {
        const { data: kpiDefs } = await supabase
          .from('squad_kpi_definitions')
          .select('id')
          .eq('role', member.role);

        const totalForMember = kpiDefs?.length || 0;

        const { data: completedKPIs } = await supabase
          .from('squad_member_kpis')
          .select('status')
          .eq('squad_member_id', member.id)
          .in('status', ['approved', 'completed']);

        const completedForMember = completedKPIs?.length || 0;

        totalCompleted += completedForMember;
        totalKPIs += totalForMember;

        memberProgressList.push({
          id: member.id,
          name: member.full_name,
          role: member.role,
          email: member.email,
          completedKPIs: completedForMember,
          totalKPIs: totalForMember,
          percentage: totalForMember > 0 ? (completedForMember / totalForMember) * 100 : 0,
          assessmentCompleted: member.assessment_completed || false
        });
      }

      setMembers(memberProgressList);
      setCompletedKPIsTotal(totalCompleted);
      setTotalKPIsTotal(totalKPIs);
      setOverallProgress(totalKPIs > 0 ? (totalCompleted / totalKPIs) * 100 : 0);

      // 2. Load assigned problems with selection dates
      const { data: problemsData } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('status', 'assigned')
        .contains('assignment', JSON.stringify({ squadId: squadUser?.squadId }));

      if (problemsData) {
        const assigned = problemsData.map(problem => ({
          id: problem.id,
          title: problem.title,
          status: problem.status,
          assignedAt: problem.assigned_at || problem.created_at,
          selectedAt: problem.selected_at || problem.assigned_at,
          targetDate: problem.assignment?.targetDeliveryDate,
          milestones: problem.assignment?.milestones || [],
          solutionExpectations: problem.assignment?.solutionExpectations || null
        }));
        setAssignedProblems(assigned);
      }

      // 3. Load upcoming tasks for squad members
      const memberIds = membersData?.map(m => m.id) || [];
      if (memberIds.length > 0) {
        const { data: tasksData } = await supabase
          .from('squad_tasks')
          .select('*, squad_members!inner(full_name)')
          .in('squad_member_id', memberIds)
          .eq('status', 'pending')
          .order('due_date', { ascending: true })
          .limit(5);

        if (tasksData) {
          const tasks = tasksData.map(task => ({
            id: task.id,
            title: task.title,
            description: task.description || '',
            dueDate: task.due_date,
            priority: task.priority,
            status: task.status,
            assignedTo: task.squad_member_id,
            assignedToName: task.squad_members?.full_name || 'Unknown'
          }));
          setUpcomingTasks(tasks);
        }
      }

      // 4. Load customer meetings
      const { data: meetingsData } = await supabase
        .from('customer_meetings')
        .select('*')
        .eq('squad_id', squadUser?.squadId)
        .order('meeting_date', { ascending: false })
        .limit(5);

      if (meetingsData) {
        setCustomerMeetings(meetingsData);
      }

      // 5. Load LOIs
      const { data: loisData } = await supabase
        .from('letters_of_intent')
        .select('*')
        .eq('squad_id', squadUser?.squadId);

      if (loisData) {
        setLois(loisData);
      }

    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate milestone progress for a problem
  const getMilestoneProgress = (milestones: any[]) => {
    if (!milestones || milestones.length === 0) return 0;
    const completed = milestones.filter(m => m.completed).length;
    return Math.round((completed / milestones.length) * 100);
  };

  // Calculate overall milestone progress across all problems
  const getOverallMilestoneProgress = () => {
    let totalMilestones = 0;
    let completedMilestones = 0;
    assignedProblems.forEach(problem => {
      if (problem.milestones) {
        totalMilestones += problem.milestones.length;
        completedMilestones += problem.milestones.filter(m => m.completed).length;
      }
    });
    return totalMilestones > 0 ? Math.round((completedMilestones / totalMilestones) * 100) : 0;
  };

  // Get the earliest problem selection date for timeline
  const getProblemSelectionDate = () => {
    if (assignedProblems.length === 0) return null;
    
    const selectedDates = assignedProblems
      .map(p => p.selectedAt ? new Date(p.selectedAt) : null)
      .filter(d => d !== null);
    
    if (selectedDates.length === 0) return null;
    
    // Return the earliest selection date
    return new Date(Math.min(...selectedDates.map(d => d!.getTime())));
  };

  // Calculate days remaining based on problem selection date
  const getDaysRemaining = () => {
    const startDate = getProblemSelectionDate();
    
    if (!startDate) {
      return { 
        daysPassed: 0, 
        daysRemaining: 90, 
        totalDays: 90, 
        hasStarted: false,
        startDate: null,
        endDate: null
      };
    }
    
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 90);
    
    const today = new Date();
    const totalDays = 90;
    let daysPassed = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Clamp values
    daysPassed = Math.max(0, Math.min(daysPassed, totalDays));
    const daysRemaining = Math.max(0, totalDays - daysPassed);
    
    return { 
      daysPassed, 
      daysRemaining, 
      totalDays, 
      hasStarted: true,
      startDate,
      endDate
    };
  };

  const { daysPassed, daysRemaining, totalDays, hasStarted, startDate } = getDaysRemaining();
  const timeProgress = hasStarted ? (daysPassed / totalDays) * 100 : 0;
  const milestoneProgress = getOverallMilestoneProgress();

  // Get current phase based on days passed
  const getCurrentPhase = () => {
    if (!hasStarted) return { name: 'Not Started', icon: '⏳', description: 'Select a problem to begin your 90-day journey' };
    if (daysPassed < 20) return { name: 'Problem Selection', icon: '🎯', description: 'Understanding the problem and conducting research' };
    if (daysPassed < 45) return { name: 'Solution Development', icon: '💡', description: 'Developing solution concepts and prototypes' };
    if (daysPassed < 70) return { name: 'Validation', icon: '🔬', description: 'Testing solutions with customers' };
    if (daysPassed < 90) return { name: 'Graduation Prep', icon: '📊', description: 'Finalizing solution and preparing pitch' };
    return { name: 'Graduation', icon: '🎉', description: 'Ready for Demo Day!' };
  };

  const currentPhase = getCurrentPhase();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/forge/dashboard')} 
            className="gap-2 -ml-3"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-foreground mt-2">Progress Tracker</h1>
          <p className="text-muted-foreground">
            Track your squad's journey through The Forge 90-day program
          </p>
        </div>
        <div className="text-right">
          <Badge className="bg-primary/10 text-primary">
            Squad: {squadUser?.squadName}
          </Badge>
        </div>
      </div>

      {/* Overall Progress Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">KPI Progress</p>
                <p className="text-xl md:text-2xl font-bold">{Math.round(overallProgress)}%</p>
              </div>
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Target className="h-4 w-4 md:h-5 md:w-5 text-primary" />
              </div>
            </div>
            <Progress value={overallProgress} className="h-1.5 md:h-2 mt-2 md:mt-3" />
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">
              {completedKPIsTotal}/{totalKPIsTotal} KPIs completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Milestone Progress</p>
                <p className="text-xl md:text-2xl font-bold">{milestoneProgress}%</p>
              </div>
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Flag className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
              </div>
            </div>
            <Progress value={milestoneProgress} className="h-1.5 md:h-2 mt-2 md:mt-3" />
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">
              Across {assignedProblems.length} assigned problems
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">90-Day Timeline</p>
                <p className="text-xl md:text-2xl font-bold">
                  {hasStarted ? `Day ${daysPassed}` : 'Not Started'}
                </p>
              </div>
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
              </div>
            </div>
            <Progress value={timeProgress} className="h-1.5 md:h-2 mt-2 md:mt-3" />
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">
              {hasStarted ? `${daysRemaining} days remaining` : 'Select a problem to start'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Team Members</p>
                <p className="text-xl md:text-2xl font-bold">{members.length}</p>
              </div>
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="h-4 w-4 md:h-5 md:w-5 text-purple-600" />
              </div>
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">
              {members.filter(m => m.assessmentCompleted).length} assessments completed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">LOIs Secured</p>
                <p className="text-xl md:text-2xl font-bold">{lois.length}/3</p>
              </div>
              <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <FileCheck className="h-4 w-4 md:h-5 md:w-5 text-yellow-600" />
              </div>
            </div>
            <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">
              Letters of Intent target
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" className="text-xs md:text-sm">Overview</TabsTrigger>
          <TabsTrigger value="problems" className="text-xs md:text-sm">Assigned Problems</TabsTrigger>
          <TabsTrigger value="members" className="text-xs md:text-sm">Member Progress</TabsTrigger>
          <TabsTrigger value="tasks" className="text-xs md:text-sm">Weekly Tasks ({upcomingTasks.length})</TabsTrigger>
          <TabsTrigger value="meetings" className="text-xs md:text-sm">Customer Meetings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Phase Timeline Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Rocket className="h-5 w-5" />
                90-Day Journey
              </CardTitle>
              <CardDescription>
                {hasStarted 
                  ? `Started on ${startDate?.toLocaleDateString()} • ${daysRemaining} days remaining`
                  : "The 90-day countdown begins when your squad selects a problem"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!hasStarted ? (
                <div className="text-center py-8 bg-muted/30 rounded-lg">
                  <Target className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Journey not yet started</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your 90-day countdown will begin once your squad selects a problem to solve
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/forge/problem-bank')}
                  >
                    Browse Problem Bank
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Phase labels with color coding */}
                  <div className="grid grid-cols-5 gap-1 text-center">
                    {[
                      { name: "Formation", day: 0 },
                      { name: "Problem Selection", day: 20 },
                      { name: "Solution Dev", day: 45 },
                      { name: "Validation", day: 70 },
                      { name: "Graduation", day: 90 }
                    ].map((phase) => {
                      const isCompleted = daysPassed >= phase.day;
                      const isCurrent = daysPassed < phase.day && (phase.day === 0 || daysPassed >= [0,20,45,70][Math.max(0, [0,20,45,70].indexOf(phase.day) - 1)]);
                      return (
                        <div 
                          key={phase.name}
                          className={`text-[10px] md:text-xs font-medium px-1 py-1 rounded ${
                            isCompleted 
                              ? 'text-green-700 bg-green-50' 
                              : isCurrent 
                                ? 'text-yellow-700 bg-yellow-50' 
                                : 'text-muted-foreground bg-muted/30'
                          }`}
                        >
                          {phase.name}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Progress bar */}
                  <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="absolute h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(timeProgress, 100)}%` }}
                    />
                    {/* Phase markers */}
                    <div className="absolute top-0 left-0 w-full h-full">
                      <div className="absolute left-[22%] top-0 w-0.5 h-full bg-white/50" />
                      <div className="absolute left-[50%] top-0 w-0.5 h-full bg-white/50" />
                      <div className="absolute left-[78%] top-0 w-0.5 h-full bg-white/50" />
                    </div>
                  </div>
                  
                  {/* Day indicators */}
                  <div className="grid grid-cols-5 gap-1 text-center text-[10px] md:text-xs text-muted-foreground">
                    <span>Day 1</span>
                    <span>Day 20</span>
                    <span>Day 45</span>
                    <span>Day 70</span>
                    <span>Day 90</span>
                  </div>
                  
                  {/* Current status message */}
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm flex items-center gap-2">
                      <span className="text-lg">{currentPhase.icon}</span>
                      <span className="font-medium">{currentPhase.name} Phase:</span>
                      <span className="text-muted-foreground">{currentPhase.description}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Day {daysPassed} of 90 • {Math.round((daysPassed / 90) * 100)}% complete
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Problems Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5" />
                Assigned Problems ({assignedProblems.length})
              </CardTitle>
              <CardDescription>Problems your squad is currently solving</CardDescription>
            </CardHeader>
            <CardContent>
              {assignedProblems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No problems assigned yet</p>
                  <p className="text-sm">Problems will appear here once assigned by an analyst</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignedProblems.map((problem) => {
                    const problemMilestoneProgress = getMilestoneProgress(problem.milestones || []);
                    return (
                      <div 
                        key={problem.id} 
                        className="p-4 border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => navigate(`/forge/problem/${problem.id}`)}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-2">
                          <div className="flex-1">
                            <h3 className="font-semibold">{problem.title}</h3>
                            <div className="flex flex-wrap items-center gap-3 mt-1">
                              <Badge className="bg-purple-100 text-purple-700">
                                {problem.status}
                              </Badge>
                              {problem.targetDate && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  Target: {new Date(problem.targetDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">{problemMilestoneProgress}%</p>
                            <p className="text-xs text-muted-foreground">Milestones</p>
                          </div>
                        </div>
                        <Progress value={problemMilestoneProgress} className="h-1.5 mt-3" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  KPI Completion by Role
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {members.map((member) => (
                  <div key={member.id}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{getDisplayRole(member.role)}</span>
                      <span className="text-muted-foreground">{member.completedKPIs}/{member.totalKPIs}</span>
                    </div>
                    <Progress value={member.percentage} className="h-2" />
                  </div>
                ))}
                {members.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">No members found</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-500" />
                  Customer Engagement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span>Customer Meetings</span>
                    <span className="font-bold text-blue-600">{customerMeetings.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>LOIs Secured</span>
                    <span className="font-bold text-yellow-600">{lois.length}/3</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Pending Tasks</span>
                    <span className="font-bold text-orange-600">{upcomingTasks.length}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Assigned Problems Tab - Detailed View */}
        <TabsContent value="problems" className="space-y-4">
          {assignedProblems.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No problems assigned yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Problems will appear here once analysts assign them to your squad
                </p>
              </CardContent>
            </Card>
          ) : (
            assignedProblems.map((problem) => {
              const problemMilestoneProgress = getMilestoneProgress(problem.milestones || []);
              const completedMilestones = problem.milestones?.filter(m => m.completed).length || 0;
              const totalMilestones = problem.milestones?.length || 0;
              
              return (
                <Card 
                  key={problem.id} 
                  className="cursor-pointer hover:shadow-md transition-all"
                  onClick={() => navigate(`/forge/problem/${problem.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{problem.title}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <Badge className="bg-purple-100 text-purple-700">
                            {problem.status}
                          </Badge>
                          {problem.selectedAt && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              Selected: {new Date(problem.selectedAt).toLocaleDateString()}
                            </span>
                          )}
                          {problem.targetDate && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Hourglass className="h-3 w-3" />
                              Target: {new Date(problem.targetDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        {/* Milestone breakdown */}
                        {totalMilestones > 0 && (
                          <div className="mt-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-muted-foreground">Milestone Progress</span>
                              <span>{completedMilestones}/{totalMilestones} completed</span>
                            </div>
                            <Progress value={problemMilestoneProgress} className="h-2" />
                          </div>
                        )}
                        
                        {/* Expected outcome preview */}
                        {problem.solutionExpectations?.expectedOutcome && (
                          <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                            <span className="font-medium">Expected outcome:</span> {problem.solutionExpectations.expectedOutcome}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-2" />
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Member Progress Tab */}
        <TabsContent value="members" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map((member) => (
              <Card 
                key={member.id} 
                className="cursor-pointer hover:shadow-lg transition-all duration-300"
                onClick={() => navigate(`/forge/squad/${member.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-lg font-bold ${getRoleBadgeColor(member.role)}`}>
                      {getInitials(member.name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{member.name}</h3>
                      <Badge className={getRoleBadgeColor(member.role)}>
                        {getDisplayRole(member.role)}
                      </Badge>
                      {member.assessmentCompleted && (
                        <Badge variant="outline" className="ml-2 bg-green-50 text-green-700">
                          ✓ Assessed
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-muted-foreground">KPI Progress</span>
                      <span>{member.completedKPIs}/{member.totalKPIs}</span>
                    </div>
                    <Progress value={member.percentage} className="h-1.5" />
                  </div>
                  <div className="mt-3 text-right">
                    <span className="text-xs text-primary inline-flex items-center gap-1">
                      View profile <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {members.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No squad members found</p>
            </div>
          )}
        </TabsContent>

        {/* Weekly Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          {upcomingTasks.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <ListChecks className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No pending tasks</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Tasks will appear here when assigned by your squad lead or analyst
                </p>
              </CardContent>
            </Card>
          ) : (
            upcomingTasks.map((task) => (
              <Card key={task.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1">
                      <h3 className="font-medium">{task.title}</h3>
                      {task.description && (
                        <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <Badge className={
                          task.priority === 'high' ? 'bg-red-100 text-red-700' :
                          task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-blue-100 text-blue-700'
                        }>
                          {task.priority}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          Assigned to: {task.assignedToName}
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Customer Meetings Tab */}
        <TabsContent value="meetings" className="space-y-4">
          {customerMeetings.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No customer meetings recorded</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Meetings will appear here once you conduct customer interviews
                </p>
              </CardContent>
            </Card>
          ) : (
            customerMeetings.map((meeting) => (
              <Card key={meeting.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1">
                      <h3 className="font-medium">{meeting.title}</h3>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(meeting.meetingDate).toLocaleDateString()}
                        </span>
                      </div>
                      {meeting.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                          {meeting.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          
          {/* LOIs Section */}
          {lois.length > 0 && (
            <Card className="border-l-4 border-l-yellow-500">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-yellow-600" />
                  Letters of Intent ({lois.length}/3)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lois.map((loi) => (
                  <div key={loi.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div>
                      <p className="font-medium">{loi.companyName}</p>
                      {loi.signedDate && (
                        <p className="text-xs text-muted-foreground">
                          Signed: {new Date(loi.signedDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Badge className="bg-green-100 text-green-700">
                      {loi.status}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}